/**
 * Toast Component
 * Displays styled notifications with optional actions
 */

import React, { useEffect } from "react";

export interface ToastProps {
    id?: string;
    message: string;
    type?: "success" | "error" | "info" | "warning";
    duration?: number;
    onClose?: () => void;
    action?: {
        label: string;
        onClick: () => void;
    };
}

export const Toast: React.FC<ToastProps> = ({
    message,
    type = "info",
    duration = 5000,
    onClose,
    action,
}) => {
    useEffect(() => {
        if (duration > 0 && onClose) {
            const timer = setTimeout(onClose, duration);
            return () => clearTimeout(timer);
        }
    }, [duration, onClose]);

    const getBackgroundColor = () => {
        switch (type) {
            case "success":
                return "#10b981";
            case "error":
                return "#ef4444";
            case "warning":
                return "#f59e0b";
            case "info":
            default:
                return "#3b82f6";
        }
    };

    const containerStyle: React.CSSProperties = {
        backgroundColor: getBackgroundColor(),
        color: "white",
        padding: "16px 20px",
        borderRadius: "8px",
        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
        display: "flex",
        alignItems: "center",
        gap: "12px",
        minWidth: "300px",
        maxWidth: "500px",
    };

    const messageStyle: React.CSSProperties = {
        flex: 1,
        fontSize: "14px",
        lineHeight: "1.5",
    };

    const actionStyle: React.CSSProperties = {
        backgroundColor: "rgba(255, 255, 255, 0.2)",
        border: "none",
        borderRadius: "4px",
        padding: "6px 12px",
        color: "white",
        fontSize: "14px",
        fontWeight: 500,
        cursor: "pointer",
        transition: "background-color 0.2s",
    };

    const closeButtonStyle: React.CSSProperties = {
        backgroundColor: "transparent",
        border: "none",
        color: "white",
        fontSize: "20px",
        cursor: "pointer",
        padding: "0",
        width: "24px",
        height: "24px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        opacity: 0.8,
        transition: "opacity 0.2s",
    };

    return (
        <>
            <style>
                {`
                    @keyframes slideIn {
                        from {
                            transform: translateX(100%);
                            opacity: 0;
                        }
                        to {
                            transform: translateX(0);
                            opacity: 1;
                        }
                    }
                `}
            </style>
            <div style={containerStyle}>
                <div style={messageStyle}>{message}</div>
                {action && (
                    <button
                        style={actionStyle}
                        onClick={action.onClick}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor =
                                "rgba(255, 255, 255, 0.3)";
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor =
                                "rgba(255, 255, 255, 0.2)";
                        }}
                    >
                        {action.label}
                    </button>
                )}
                {onClose && (
                    <button
                        style={closeButtonStyle}
                        onClick={onClose}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.opacity = "1";
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.opacity = "0.8";
                        }}
                    >
                        ×
                    </button>
                )}
            </div>
        </>
    );
};
